import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Shield, Leaf, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackPageView } from '../lib/analytics';
import { useCMS } from '../hooks/useCMS';

const VALUES = [
  {
    icon: Heart,
    title: '專業',
    description: '以工程標準嚴選產品，專業嘢先上架。',
    bgKey: 'story.values.background1',
    defaultBg: '',
  },
  {
    icon: Shield,
    title: '可靠',
    description: '穩定供應、售後有跟進，買得放心。',
    bgKey: 'story.values.background2',
    defaultBg: '',
  },
  {
    icon: Leaf,
    title: '實用',
    description: '唔賣花巧，每一件都係用得著嘅裝備。',
    bgKey: 'story.values.background3',
    defaultBg: '',
  },
];

export default function Story() {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => {
    trackPageView('/story');
  }, []);

  const { getValue, getHtml, loading: cmsLoading } = useCMS();

  const galleryImages = [
    {
      src: getValue('story.gallery.image1', ''),
      caption: getValue('story.gallery.caption1', ''),
      span: 'md:col-span-2 md:row-span-2',
    },
    {
      src: getValue('story.gallery.image2', ''),
      caption: getValue('story.gallery.caption2', ''),
      span: '',
    },
    {
      src: getValue('story.gallery.image3', ''),
      caption: getValue('story.gallery.caption3', ''),
      span: '',
    },
    {
      src: getValue('story.gallery.image4', ''),
      caption: getValue('story.gallery.caption4', ''),
      span: '',
    },
    {
      src: getValue('story.gallery.image5', ''),
      caption: getValue('story.gallery.caption5', ''),
      span: 'md:col-span-2',
    },
  ];

  // 淨係顯示後台設定咗圖嘅格（story.gallery.imageN 空＝跳過）
  const visibleGallery = galleryImages.filter((i) => i.src);

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">載入中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-x1493-dark text-white py-20 md:py-28 overflow-hidden">
        {getValue('story.hero.image', '') && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${getValue('story.hero.image', '')}')` }}
          />
        )}
        <div className="absolute inset-0 bg-x1493-dark/70" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <p className="text-x1493-secondary font-bold tracking-widest mb-4">品牌故事</p>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            dangerouslySetInnerHTML={getHtml('story.hero.title', 'Kare Tech 品牌故事<br />技術人嘅補給站')}
          />
          <p
            className="text-lg text-white/80 leading-relaxed max-w-2xl mx-auto"
            dangerouslySetInnerHTML={getHtml('story.hero.subtitle', 'Kare Tech 由技術人出發，明白工程現場要嘅唔係花巧，而係靠得住嘅裝備。我哋嚴選 IT 產品同工程器材，由選貨、測試到上架，每一步都專業把關。')}
          />
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              dangerouslySetInnerHTML={getHtml('story.gallery.title', '我哋做嘅嘢，每一步都看得見')}
            />
            <p
              className="text-stone-600 max-w-2xl mx-auto"
              dangerouslySetInnerHTML={getHtml('story.gallery.description', '持續引入新產品，同你一齊睇住個店成長。')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[200px]">
            {visibleGallery.length === 0 ? (
              <p className="text-stone-400 text-sm">圖片整理中，稍後上載。</p>
            ) : visibleGallery.map((item, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setLightboxImage(item.src)}
                className={`relative rounded-2xl overflow-hidden group text-left ${item.span || ''}`}
              >
                <img
                  src={item.src}
                  alt={item.caption}
                  className="absolute inset-0 w-full h-full object-cover transition duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300">
                  <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full">
                    點擊放大
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <p className="text-white font-medium text-sm md:text-base leading-snug">
                    {item.caption}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Lightbox */}
          {lightboxImage && (
            <div
              className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
              onClick={() => setLightboxImage(null)}
            >
              <button
                type="button"
                onClick={() => setLightboxImage(null)}
                className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
                aria-label="關閉"
              >
                <X size={32} />
              </button>
              <img
                src={lightboxImage}
                alt="放大預覽"
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </div>
      </section>

      {/* Brand Values */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              dangerouslySetInnerHTML={getHtml('story.values.title', '我們的堅持')}
            />
            <p
              className="text-stone-600"
              dangerouslySetInnerHTML={getHtml('story.values.subtitle', '專業、可靠、實用——三個堅持。')}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {VALUES.map(({ icon: Icon, title, description, bgKey, defaultBg }) => (
              <div
                key={title}
                className="relative rounded-2xl p-8 overflow-hidden min-h-[320px] flex flex-col group"
              >
                {getValue(bgKey, defaultBg) ? (
                  <img
                    src={getValue(bgKey, defaultBg)}
                    alt={title}
                    className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-x1493-dark to-x1493-primary/60" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-black/40" />
                <div className="relative z-10 flex flex-col h-full">
                  <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="text-x1493-secondary" size={28} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{title}</h3>
                  <p className="text-white/80">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-x1493-primary text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            dangerouslySetInnerHTML={getHtml('story.cta.title', '準備好裝備你嘅項目？')}
          />
          <p
            className="text-white/90 mb-8"
            dangerouslySetInnerHTML={getHtml('story.cta.subtitle', '去商店逛逛，搵出你嘅趁手工具。')}
          />
          <Link to="/store" className="inline-flex items-center gap-2 bg-white text-x1493-primary font-bold px-8 py-4 rounded-2xl hover:bg-stone-100 transition">
            {getValue('story.cta.buttonText', '逛商店')}
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
